"use client";

import { faFileArrowDown, faLocationCrosshairs } from "@fortawesome/pro-solid-svg-icons";
import { Form, Typography, useZodForm } from "@tosize/ui";
import type { PropsWithChildren } from "react";
import { z } from "zod";
import type { LangParams } from "~/app/[lang]/layout";
import { StyledLink } from "~/components/Link";
import { useBetterAction } from "~/hooks/useBetterAction";
import { getOrderStatus } from "./actions";

type Labels =
	| "status"
	| "delivery"
	| "shipper"
	| "address"
	| "files"
	| "invoice"
	| "credit"
	| "shipment"
	| "tracking";

interface TrackingFormProps extends LangParams, PropsWithChildren {
	labels: Record<Labels, string>;
}

export function TrackingForm({ lang, children, labels }: TrackingFormProps) {
	const {
		executeAsync,
		result: { data },
	} = useBetterAction(getOrderStatus);

	const form = useZodForm({
		schema: z.object({
			order: z.string().min(9),
			email: z.string().email(),
		}),
	});

	const deliveryDate = data?.deliveryDate && new Date(data.deliveryDate);

	if (data) {
		return (
			<div className="flex flex-col gap-6">
				{!!data.statusLabel && (
					<div>
						<Typography variant="paragraph-bold">{labels.status}</Typography>
						<Typography variant="paragraph">{data.statusLabel}</Typography>
					</div>
				)}

				{!!data.shippingDescription && (
					<div>
						<Typography variant="paragraph-bold">{labels.shipper}</Typography>
						<Typography variant="paragraph">{data.shippingDescription}</Typography>
					</div>
				)}

				{!!data.deliveryDate && (
					<div>
						<Typography variant="paragraph-bold">{labels.delivery}</Typography>
						<Typography variant="paragraph">
							{deliveryDate
								? deliveryDate.toLocaleString(lang, {
										weekday: "short",
										day: "numeric",
										month: "short",
										year: "numeric",
									})
								: null}
						</Typography>
					</div>
				)}

				{!!data.tracking.length && (
					<div>
						<Typography variant="paragraph-bold">{labels.tracking}</Typography>
						<ul>
							{data.tracking?.map((track, index) => (
								<li key={index}>
									<StyledLink target="_blank" starticon={faLocationCrosshairs} href={track.url}>
										{track.label}
									</StyledLink>
								</li>
							))}
						</ul>
					</div>
				)}

				{data.shippingAddress && (
					<div>
						<Typography variant="paragraph-bold">{labels.address}</Typography>

						{!!data.shippingAddress.street && (
							<Typography variant="paragraph">{data.shippingAddress.street}</Typography>
						)}

						{!!data.shippingAddress.postcode && (
							<Typography variant="paragraph">
								{data.shippingAddress.postcode}&nbsp;
								{data.shippingAddress.city}
							</Typography>
						)}

						{!!data.shippingAddress.country?.label && (
							<Typography variant="paragraph">{data.shippingAddress?.country.label}</Typography>
						)}
					</div>
				)}

				<div>
					<Typography variant="paragraph-bold">{labels.files}</Typography>
					<ul>
						{!!data.invoiceDownloadUrl && (
							<li>
								<StyledLink
									target="_blank"
									starticon={faFileArrowDown}
									href={data.invoiceDownloadUrl}
								>
									{labels.invoice}
								</StyledLink>
							</li>
						)}
						{!!data.creditDownloadUrl && (
							<li>
								<StyledLink
									target="_blank"
									starticon={faFileArrowDown}
									href={data.creditDownloadUrl}
								>
									{labels.credit}
								</StyledLink>
							</li>
						)}
						{!!data.shipmentDownloadUrl && (
							<li>
								<StyledLink
									target="_blank"
									starticon={faFileArrowDown}
									href={data.shipmentDownloadUrl}
								>
									{labels.shipment}
								</StyledLink>
							</li>
						)}
					</ul>
				</div>
			</div>
		);
	}

	return (
		<Form
			form={form}
			className="flex flex-col gap-6"
			onSubmit={async (data) => await executeAsync({ lang, ...data })}
		>
			{children}
		</Form>
	);
}
